// Popover for picking a page icon (emoji or image URL).
//
// See client/src/sandbox/IconPicker.tsx for the original — this is the live
// version with no behavioral differences. Curated emoji set, custom-input
// row for arbitrary emoji or image URLs, Remove + Close at the bottom.

import { useEffect, useRef, useState } from 'react';
import { RiCloseLine, RiDeleteBinLine } from 'react-icons/ri';

interface IconPickerProps {
  value: string | null;
  onChange: (next: string | null) => void;
  onClose: () => void;
}

const EMOJIS = [
  '📝', '📋', '📄', '📃', '📜', '📑', '🗒️', '📅', '📆', '📇',
  '📊', '📈', '📉', '📁', '📂', '📌', '📍', '📎', '🔖', '🏷️',
  '💬', '📧', '📞', '✉️', '📢', '🔔', '📣', '📤', '📥', '📭',
  '👥', '🤝', '👋', '🙌', '👏', '💪', '🧠', '🧑‍💻',
  '✅', '❌', '⚠️', '❗', '❓', '💯', '🚫', '🆕', '🆗', '🔴',
  '🔄', '⏰', '⏳', '🔒', '🔓', '🔑', '🛠️', '⚙️', '🔧', '🔨',
  '💡', '🧩', '🎯', '🎓', '🏆', '🌟', '✨', '⭐', '🚀', '🔥',
  '🙂', '😀', '🤔', '😎', '🥳', '😴', '🤓', '🧐',
  '🏠', '🏢', '💼', '☕', '🌍', '💰', '📚', '📖', '🔗', '🌈',
];

export function IconPicker({ value, onChange, onClose }: IconPickerProps) {
  const [custom, setCustom] = useState(value ?? '');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
    inputRef.current?.select();
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  function pick(next: string | null) {
    onChange(next);
    onClose();
  }

  function commitCustom() {
    const trimmed = custom.trim();
    if (!trimmed) return;
    pick(trimmed);
  }

  return (
    <div
      role="dialog"
      aria-label="Pick page icon"
      className="absolute z-30 left-0 top-full mt-1 w-[360px] max-w-[92vw] bg-white border border-brand-200 shadow-xl"
      onMouseDown={(e) => e.stopPropagation()}
    >
      <div className="px-3 py-2 border-b border-brand-100 flex items-center gap-2">
        <input
          ref={inputRef}
          type="text"
          value={custom}
          onChange={(e) => setCustom(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              commitCustom();
            }
          }}
          placeholder="Emoji or image URL"
          className="flex-1 text-sm px-2 py-1.5 border border-brand-200 outline-none focus:border-brand-500 placeholder:text-brand-300 bg-white"
        />
        <button
          type="button"
          onClick={commitCustom}
          disabled={!custom.trim()}
          className="px-2 py-1.5 text-xs bg-brand-600 text-white hover:bg-brand-700 disabled:opacity-40"
        >
          Set
        </button>
      </div>

      <div className="p-2 grid grid-cols-9 gap-0.5 max-h-64 overflow-y-auto">
        {EMOJIS.map((emoji, i) => (
          <button
            key={i}
            type="button"
            onClick={() => pick(emoji)}
            className={`w-8 h-8 text-xl hover:bg-brand-50 flex items-center justify-center transition-colors ${
              emoji === value ? 'bg-brand-100' : ''
            }`}
            title={`Pick ${emoji}`}
            aria-label={`Pick ${emoji}`}
          >
            {emoji}
          </button>
        ))}
      </div>

      <div className="px-3 py-2 border-t border-brand-100 flex justify-between items-center bg-brand-50">
        <button
          type="button"
          onClick={() => pick(null)}
          disabled={!value}
          className="text-xs text-brand-600 hover:text-brand-800 disabled:opacity-40 inline-flex items-center gap-1"
        >
          <RiDeleteBinLine size={12} />
          Remove icon
        </button>
        <button
          type="button"
          onClick={onClose}
          className="text-xs text-brand-500 hover:text-brand-700 inline-flex items-center gap-1"
        >
          <RiCloseLine size={12} />
          Close
        </button>
      </div>
    </div>
  );
}
